import { useEffect, useState, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import DatePicker from 'react-datepicker'
import clsx from 'clsx'

import {
  RainfallChart,
  VegetronixVH400Chart,
  GenericLineChart,
  FlowMeterChart,
  VegetronixTHERM200Chart,
} from '../components/charts'
import { useAccountContext, useAppContext } from '../contexts'
import { DeviceDetails } from '../components'
import { Spinner } from '../components/Spinner'
import { api } from '../services'
import { SensorProduct, UnitCategoryId } from '../common/enums'
import { BadgerSDIChart } from '../components/charts/BadgerSDIChart'
import { DeviceSensorSelector } from '../components/DeviceSensorSelector'
import { Switch } from '../components/Switch'
import {
  MANAGE_DATA_ENABLE_ADVANCED_FEATURES,
  MANAGE_DATA_SHOW_ADJUSTMENTS,
  MANAGE_DATA_SHOW_ADVANCED_CONTROLS,
  MANAGE_DATA_SHOW_EXACT_TIMESTAMPS,
  MANAGE_DATA_SHOW_TIMESTAMPS,
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
  SCHD_INTERVAL_MINUTES,
} from '../common/constants'
import { Viewport } from '../common/types'
import { useDeviceReadings } from '../hooks/useDeviceReadings'

interface Props {
  sigfoxId: string
  viewport: Viewport
}

export function ManageDataByCharts({ sigfoxId, viewport }: Props) {
  const { currentDate } = useAppContext()
  const {
    devices,
    userUnitSettings,
    unitConversions,
    selectedDevice,
    // getDevice
  } = useAccountContext()

  const [sensorConfigs, setSensorConfigs] = useState<Record<string, any>>({})

  const [currentTime, setCurrentTime] = useState(new Date().getTime())
  const [selectedSensorPort, setSelectedSensorPort] = useState('')
  const [manageDataMode, setManageDataMode] = useState(true)
  const [showAllData, setShowAllData] = useState(true)

  const [startTimestamp, setStartTimestamp] = useState<number>(0)
  const [endTimestamp, setEndTimestamp] = useState<number>(0)

  const [hideForAllSensors, setHideForAllSensors] = useState(true)
  const [timestampAdjustment, setTimestampAdjustment] = useState(5 * 60 * 1000)
  const [resolution, setResolution] = useState(ONE_DAY)

  const device = devices[sigfoxId]

  const refreshTrigger = currentDate

  // Calculate query parameters from viewport
  const from = useMemo(() => Math.round(viewport.minimum / 1000), [viewport.minimum])
  const to = useMemo(() => Math.round(viewport.maximum / 1000), [viewport.maximum])
  const limit = useMemo(() => {
    const totalDays = (to - from) / (60 * 60 * 24)
    const numberOfDataPoints = totalDays * 24 * (60 / SCHD_INTERVAL_MINUTES)
    return Math.round(numberOfDataPoints * 2)
  }, [from, to])

  // Use hook for cached readings
  // isFetching is true during refetches (date changes), isLoading only on initial load
  const { data: readingsData, isFetching: isReadingsFetching } = useDeviceReadings(sigfoxId, from, to, limit, !!sigfoxId)

  // Extract readings from hook response
  const readings = readingsData?.sensors

  useEffect(() => {
    setSensorConfigs({})
    setSelectedSensorPort('')
    setStartTimestamp(0)
    setEndTimestamp(0)
  }, [sigfoxId])

  // Fetch sensor configs separately (not part of readings cache)
  useEffect(() => {
    async function fetchSensorConfigs() {
      if (!sigfoxId) return

      try {
        setSensorConfigs({})
        setCurrentTime(new Date().getTime())
        const sensorConfig = await api.getSensorConfigs(sigfoxId, to)
        setSensorConfigs(sensorConfig.data)
      } catch (error) {
        console.error(error)
      }
    }

    fetchSensorConfigs()
  }, [refreshTrigger, selectedDevice, viewport, sigfoxId, to])

  // Refetch function for after hide/unhide operations
  async function refetchReadings() {
    try {
      const sensorConfig = await api.getSensorConfigs(sigfoxId, to)
      setSensorConfigs(sensorConfig.data)
      // The useDeviceReadings hook will automatically refetch on next mount/focus
      // due to staleTime configuration
    } catch (error) {
      console.error(error)
    }
  }

  function renderAllCharts(readings: any): JSX.Element[] {
    const charts: JSX.Element[] = []

    if (sensorConfigs === undefined || readings === undefined || Object.keys(unitConversions).length === 0) {
      return []
    }

    if (!isLoading && readings && Object.keys(readings).length === 0) {
      return [<div className={tailwind.alert}>No data available this time period</div>]
    }

    const data: Record<string, any> = {}
    let order: any[] = []

    Object.keys(readings).forEach((key: any) => {
      const [sensorPort] = key.split('-')

      if (selectedSensorPort && sensorPort === selectedSensorPort) {
        if (data[sensorPort] === undefined) {
          data[sensorPort] = []
          order.push(sensorPort)
        }

        data[sensorPort].push(readings[key])
      }
    })

    if (Object.keys(data).length > 0) {
      // filter out temperature sensor
      order = order.filter((item) => item !== 'T0')

      order.forEach((sensorPort) => {
        const unitCategoryId = data[sensorPort][0].metadata.units.category.id
        const chart: JSX.Element | null | undefined = renderIndividualChart(unitCategoryId, data[sensorPort])

        if (chart) {
          charts.push(chart)
        }
      })
    }

    return charts
  }

  function addTimestamp(timestamp: number) {
    if (startTimestamp === 0) {
      setStartTimestamp(adjust(timestamp, 'start'))
    } else if (endTimestamp === 0) {
      if (timestamp < startTimestamp) {
        setEndTimestamp(startTimestamp)
        setStartTimestamp(adjust(timestamp, 'start'))
      } else {
        setEndTimestamp(adjust(timestamp, 'end'))
      }
    } else {
      setStartTimestamp(adjust(timestamp, 'start'))
      setEndTimestamp(0)
    }
  }

  function adjust(ts: number, tsType: 'start' | 'end') {
    const date = new Date(ts)
    const offset = date.getTimezoneOffset() * ONE_MINUTE
    const localTime = ts - offset
    const adjustedTime =
      tsType === 'start'
        ? Math.floor(localTime / resolution) * resolution
        : Math.ceil(localTime / resolution) * resolution
    return adjustedTime + offset
  }

  function renderIndividualChart(unitCategoryId: number, readings: any) {
    const sensorPort = readings[0].metadata.sensorName
    const sensorProductName = readings[0].metadata.productName

    const sensorProduct =
      unitCategoryId === UnitCategoryId.WaterTemperature
        ? SensorProduct.AquaTroll500TemperatureSensor
        : Object.values(SensorProduct).find((sensorProduct) =>
            sensorProductName ? sensorProduct.includes(sensorProductName) : false
          )

    const key = `${sensorPort}-${sensorProduct?.split(' ').join('')}`

    switch (sensorProduct) {
      case SensorProduct.IrrometerWM200SS:
      case SensorProduct.AquaTroll500DissolvedOxygenSensor:
      case SensorProduct.AquaTroll500NitrateSensor:
      case SensorProduct.AquaTroll500SalinitySensor:
      case SensorProduct.AquaTroll500SpecificConductivitySensor:
      case SensorProduct.AquaTroll500TotalDissolvedSolidsSensor:
      case SensorProduct.AquaTroll500TurbiditySensor:
      case SensorProduct.AquaTroll500TemperatureSensor:
      case SensorProduct.Battery:
      case SensorProduct.Humidity:
      case SensorProduct.Pressure:
      case SensorProduct.SolarRadiation:
      case SensorProduct.Temperature:
      case SensorProduct.WindSpeed:
        return (
          <GenericLineChart
            key={key}
            device={device}
            isLoading={isLoading}
            sensorProduct={sensorProduct}
            sensorPort={sensorPort}
            sensorReadings={readings[0]}
            viewport={viewport}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
            zoomEnabled={false}
            selectXRange={[startTimestamp, endTimestamp].filter((timestamp) => timestamp !== 0)}
            addTimestamp={addTimestamp}
          />
        )

      case SensorProduct.Rainfall:
      case SensorProduct.OpticalRainSensorSAB:
      case SensorProduct.OpticalRainSensorPower:
        return (
          <RainfallChart
            key={key}
            device={device}
            sensorProduct={sensorProduct}
            sensorPort={sensorPort}
            sensorReadings={readings[0]}
            viewport={viewport}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
            isLoading={isLoading}
            zoomEnabled={false}
            selectXRange={[startTimestamp, endTimestamp].filter((timestamp) => timestamp !== 0)}
            addTimestamp={addTimestamp}
          />
        )

      case SensorProduct.VegetronixVH400:
        return (
          <VegetronixVH400Chart
            key={key}
            device={device}
            sensorPort={sensorPort}
            sensorReadings={readings[0]}
            viewport={viewport}
            isLoading={isLoading}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
            zoomEnabled={false}
            selectXRange={[startTimestamp, endTimestamp].filter((timestamp) => timestamp !== 0)}
            addTimestamp={addTimestamp}
          />
        )

      case SensorProduct.VegetronixTHERM200:
        return (
          <VegetronixTHERM200Chart
            key={key}
            device={device}
            sensorPort={sensorPort}
            sensorReadings={readings[0]}
            viewport={viewport}
            isLoading={isLoading}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
            zoomEnabled={false}
            selectXRange={[startTimestamp, endTimestamp].filter((timestamp) => timestamp !== 0)}
            addTimestamp={addTimestamp}
          />
        )

      case SensorProduct.BadgermeterSDI:
        return (
          <BadgerSDIChart
            key={key}
            device={device}
            sigfoxId={sigfoxId}
            isLoading={isLoading}
            sensorPort={sensorPort}
            sensorProduct={sensorProduct}
            // BadgerSDIChart takes the port's full reading list (it has to pick
            // a unit category out of it), unlike the single-series charts above
            sensorReadings={readings}
            viewport={viewport}
            currentTime={currentTime}
            userUnitSettings={userUnitSettings}
            zoomEnabled={false}
            selectXRange={[startTimestamp, endTimestamp].filter((timestamp) => timestamp !== 0)}
            addTimestamp={addTimestamp}
          />
        )

      case SensorProduct.Badgermeter220BR:
      case SensorProduct.DEMPaddleWheel:
        const sensorType = readings[0].metadata.productName as SensorProduct
        return (
          <FlowMeterChart
            device={device}
            sigfoxId={sigfoxId}
            isLoading={isLoading}
            sensorPort={sensorPort}
            sensorProduct={sensorType}
            sensorReadings={readings}
            viewport={viewport}
            currentTime={currentTime}
            userUnitSettings={userUnitSettings}
            zoomEnabled={false}
            selectXRange={[startTimestamp, endTimestamp].filter((timestamp) => timestamp !== 0)}
            addTimestamp={addTimestamp}
          />
        )

      default:
        // do nothing
        break
    }
    return null
  }

  // Determine loading state - use isFetching to catch both initial load AND refetches
  const isLoading = isReadingsFetching

  const simplifiedController = (
    <div className="flex flex-row flex-wrap items-center justify-center mb-3">
      <div className="w-10">{isLoading && <Spinner size={30} />}</div>

      {MANAGE_DATA_SHOW_EXACT_TIMESTAMPS ? (
        <div className="flex flex-row items-center mx-3 my-2">
          <div className="flex flex-row items-center">
            <div className={tailwind.textFieldDisabled}>{formatTimestamp(startTimestamp)}</div>
          </div>
          <div className="mx-3">{' - '}</div>
          <div className="flex flex-row items-center">
            <div className={tailwind.textFieldDisabled}>{formatTimestamp(endTimestamp)}</div>
          </div>
        </div>
      ) : (
        <div className="flex flex-row items-center mx-3 my-2">
          <div className="flex flex-row items-center">
            <DatePicker
              maxDate={new Date(endTimestamp)}
              className="p-1 text-center border-2 rounded-md cursor-pointer text-x border-slate-gray w-44"
              selected={startTimestamp !== 0 ? new Date(startTimestamp) : null}
              disabled={sigfoxId === '' || selectedDevice === '' || selectedSensorPort === ''}
              onChange={(date: Date) => {
                setStartTimestamp(date.setHours(0, 0, 0, 0))
              }}
            />
          </div>
          <div className="mx-3">{' - '}</div>
          <div className="flex flex-row items-center">
            <DatePicker
              minDate={new Date(startTimestamp + ONE_DAY)}
              maxDate={new Date()}
              className="p-1 text-center border-2 rounded-md cursor-pointer text-x border-slate-gray w-44"
              disabled={sigfoxId === '' || selectedDevice === '' || selectedSensorPort === ''}
              selected={endTimestamp !== 0 ? new Date(endTimestamp) : null}
              onChange={(date: Date) => {
                setEndTimestamp(date.setHours(0, 0, 0, 0))
              }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-row items-center mx-3 my-2">
        <button
          className={clsx('w-28', {
            [tailwind.buttonDisabled]: startTimestamp === 0 || endTimestamp === 0,
            [tailwind.buttonEnabled]: startTimestamp !== 0 && endTimestamp !== 0,
          })}
          disabled={!manageDataMode || selectedDevice === '' || startTimestamp === 0 || endTimestamp === 0}
          // button enabled when selection is active
          onClick={async () => {
            try {
              if (sigfoxId) {
                await api.hideReadings(sigfoxId, Math.round(startTimestamp / 1000), Math.round(endTimestamp / 1000))
                await refetchReadings()
              }
            } catch (error) {
              console.error(error)
            }
          }}
        >
          HIDE
        </button>
        <button
          className={clsx(
            sigfoxId === '' || selectedDevice === '' || selectedSensorPort === '' || !manageDataMode
              ? tailwind.buttonDisabled
              : tailwind.buttonEnabled
          )}
          // the selected range of data must contain hidden data before this can be enabled
          // disabled={!manageDataMode || selectedDevice === '' || startTimestamp === 0 || endTimestamp === 0}
          disabled={sigfoxId === '' || selectedDevice === '' || selectedSensorPort === ''}
          onClick={async () => {
            try {
              if (sigfoxId) {
                await api.unhideReadings(sigfoxId)
                await refetchReadings()
              }
            } catch (error) {
              console.error(error)
            }
          }}
        >
          UNHIDE ALL DATA
        </button>
      </div>
    </div>
  )

  return (
    <div className={tailwind.container}>
      <div>
        {device && <DeviceDetails device={device} showBatteryVoltage={false} open={true} />}
        {sensorConfigs && Object.keys(sensorConfigs).length > 0 && (
          <DeviceSensorSelector
            sigfoxId={sigfoxId}
            sensorConfigs={sensorConfigs}
            setSensorPort={setSelectedSensorPort}
          />
        )}
      </div>

      <div className="relative">
        {sigfoxId ? (
          renderAllCharts(readings).map((chart, index) => {
            return (
              <div key={index} className={tailwind.chart}>
                {chart}
              </div>
            )
          })
        ) : (
          <div className={clsx('my-28', tailwind.instructions)}>Select Device</div>
        )}

        {sigfoxId && !(readings && Object.keys(readings).length === 0) && selectedSensorPort === '' && (
          <div className={clsx('my-28', tailwind.instructions)}>Select Device Sensor</div>
        )}
      </div>

      <div className="flex flex-col items-center justify-center mt-10">
        <div>{simplifiedController}</div>
        <div className="hidden">
          <Switch
            label="ONE HOUR RESOLUTION"
            state={resolution === ONE_HOUR}
            onChange={() => setResolution(resolution === ONE_HOUR ? ONE_DAY : ONE_HOUR)}
          />
        </div>
      </div>

      {MANAGE_DATA_ENABLE_ADVANCED_FEATURES && (
        <div className="flex flex-col justify-center mx-auto mt-10">
          {MANAGE_DATA_SHOW_ADVANCED_CONTROLS && (
            <div className="flex flex-row items-center justify-start mb-3">
              <Switch
                label="ENABLE HIDE DATA MODE"
                state={manageDataMode}
                onChange={() => setManageDataMode(!manageDataMode)}
              />
              <Switch label="SHOW ALL DATA" state={showAllData} onChange={() => setShowAllData(!showAllData)} />
              <Switch label="PREVIEW HIDDEN DATA" state={showAllData} onChange={() => setShowAllData(!showAllData)} />
            </div>
          )}

          <div className={clsx('flex flex-col', { 'opacity-50': !manageDataMode })}>
            {simplifiedController}

            {MANAGE_DATA_SHOW_TIMESTAMPS && (
              <div className="flex flex-row items-center justify-start mb-3">
                <div className="text-right w-28">TIMESTAMPS:</div>

                <div className="flex flex-row items-center mx-3">
                  <div className="flex flex-row items-center">
                    <div className={tailwind.textFieldDisabled}>{startTimestamp}</div>
                  </div>
                  <div className="mx-3">{' - '}</div>
                  <div className="flex flex-row items-center">
                    <div className={tailwind.textFieldDisabled}>{endTimestamp}</div>
                  </div>
                </div>
                <div>
                  <button
                    className={clsx('w-28', manageDataMode ? tailwind.buttonEnabled : tailwind.buttonDisabled)}
                    disabled={!manageDataMode || selectedDevice === ''}
                    onClick={() => {
                      setEndTimestamp(0)
                      setStartTimestamp(0)
                    }}
                  >
                    CLEAR
                  </button>
                </div>
              </div>
            )}

            {MANAGE_DATA_SHOW_ADJUSTMENTS && (
              <div className={clsx('flex flex-row items-center justify-start mb-3')}>
                <div className="text-right w-28">ADJUST:</div>

                <div className="flex flex-row items-center justify-between mb-3">
                  <div className="flex flex-row items-center mx-3">
                    <div
                      className={clsx('flex flex-row items-center justify-center w-40', {
                        'opacity-20': startTimestamp === 0,
                      })}
                    >
                      <button
                        className="p-1 mx-3 border-gray-400 rounded-md"
                        disabled={startTimestamp === 0 || !manageDataMode}
                        onClick={() => {
                          setStartTimestamp(startTimestamp - timestampAdjustment)
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </button>

                      <button
                        className="p-1 mx-3 border-gray-400 rounded-md"
                        disabled={startTimestamp === 0 || !manageDataMode}
                        onClick={() => {
                          setStartTimestamp(startTimestamp + timestampAdjustment)
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </button>
                    </div>

                    <div
                      className={clsx({
                        'opacity-20': startTimestamp === 0 && endTimestamp === 0,
                      })}
                    >
                      <select
                        className={clsx('w-28 p-1 mx-3 border-gray-400 rounded-md border-1')}
                        disabled={!manageDataMode || (startTimestamp === 0 && endTimestamp === 0)}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10)
                          setTimestampAdjustment(value * 60 * 1000)
                        }}
                        defaultValue={15}
                      >
                        <option value="1">1 min</option>
                        <option value="5">5 min</option>
                        <option value="10">10 min</option>
                        <option value="15">15 min</option>
                        <option value="30">30 min</option>
                        <option value="60">1 hour</option>
                        <option value="120">2 hours</option>
                        <option value="240">4 hours</option>
                        <option value="480">8 hours</option>
                        <option value="720">12 hours</option>
                        <option value="1440">1 day</option>
                      </select>
                    </div>

                    <div
                      className={clsx('flex flex-row items-center justify-center w-40', {
                        'opacity-20': endTimestamp === 0,
                      })}
                    >
                      <button
                        className="p-1 mx-3 border-gray-400 rounded-md"
                        disabled={endTimestamp === 0 || !manageDataMode}
                        onClick={() => {
                          setEndTimestamp(endTimestamp - timestampAdjustment)
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </button>
                      <button
                        className="p-1 mx-3 border-gray-400 rounded-md"
                        disabled={endTimestamp === 0 || !manageDataMode}
                        onClick={() => {
                          setEndTimestamp(endTimestamp + timestampAdjustment)
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-row items-center justify-start mb-3">
            {MANAGE_DATA_SHOW_ADJUSTMENTS && (
              <>
                <Switch
                  label="ALL SENSORS"
                  state={hideForAllSensors}
                  onChange={() => setHideForAllSensors(!hideForAllSensors)}
                  disabled={!manageDataMode}
                />
                <button
                  className={clsx('w-28', manageDataMode ? tailwind.buttonEnabled : tailwind.buttonDisabled)}
                  disabled={!manageDataMode || selectedDevice === '' || startTimestamp === 0 || endTimestamp === 0}
                  // button enabled when selection is active
                  onClick={() => {
                    console.log('CALL API TO HIDE DATA')
                  }}
                >
                  HIDE
                </button>
                <button
                  className={clsx(manageDataMode ? tailwind.buttonEnabled : tailwind.buttonDisabled)}
                  // the selected range of data must contain hidden data before this can be enabled
                  disabled={!manageDataMode || selectedDevice === '' || startTimestamp === 0 || endTimestamp === 0}
                  onClick={() => {
                    console.log('CALL API TO UNHIDE DATA')
                  }}
                >
                  UNHIDE
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function formatTimestamp(timestamp: number) {
  return timestamp === 0 ? '-' : new Date(timestamp).toLocaleString()
}

const tailwind = {
  container: 'mx-1',
  chart: 'mb-0',
  spinner: 'relative w-full mx-auto',
  buttonEnabled:
    'bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline mb-0 m-1 w-34',
  buttonDisabled:
    'bg-gray-400 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline mb-0 m-1 w-34',
  alert: 'bg-red-500 text-white font-bold rounded px-2 py-2 mb-10 mt-6 h20 w-[320px] mx-auto text-center',
  instructions: 'bg-green-500 text-white font-bold rounded px-2 py-2 mb-10 mt-6 h20 w-[320px] mx-auto text-center',
  textFieldDisabled: 'p-1 text-center border-gray-400 rounded-md w-60 border-1 cursor-not-allowed bg-gray-200',
  textField: 'p-1 text-center border-gray-400 rounded-md w-60 border-1',
  toggleSwitchOn: 'bg-blue-500 text-white font-bold rounded px-2 py-2 mb-10 mt-6 h20 w-[320px] mx-auto text-center',
  toggleSwitchOff: 'bg-red-500 text-white font-bold rounded px-2 py-2 mb-10 mt-6 h20 w-[320px] mx-auto text-center',
}
